import React from "react";
import { XIcons, XPrim } from "../primitives.jsx";
import { XApi } from "../services.js";
import { XShell } from "../shell.jsx";
import { useT } from "../i18n.jsx";
import { renderSkillMarkdown } from "../skills/markdown.js";

const CATEGORIES = [
  "all",
  "social",
  "search",
  "crypto",
  "media",
  "data",
  "automation",
  "developer",
  "research",
];

const statusTone = (status) => {
  if (status === "PUBLISHED" || status === "APPROVED") return "ok";
  if (status === "REJECTED" || status === "SUSPENDED") return "err";
  if (status === "NEEDS_CHANGES") return "warn";
  return "neutral";
};

const StatusPill = ({ status, label }) => {
  const tone = statusTone(status);
  const color =
    tone === "ok"
      ? "var(--ok)"
      : tone === "warn"
        ? "var(--warn)"
        : tone === "err"
          ? "var(--err)"
          : "var(--fg-3)";
  return (
    <span className="tag" style={{ color }}>
      <span className={`tag-dot ${tone === "neutral" ? "" : tone}`} />
      {label || status || "DRAFT"}
    </span>
  );
};

const ACTIVE_CREATOR_STATUSES = new Set([
  "SUBMITTED",
  "IN_REVIEW",
  "PUBLISHING",
  "queued",
  "running",
  "verifying",
  "waiting_pr_merge",
]);

const latestReview = (submission, type) =>
  (submission?.reviews || []).find((review) =>
    type ? review.reviewerType === type : true,
  ) || null;

const latestPublish = (submission) => submission?.publishJobs?.[0] || null;

const localizedStatus = (t, status) => {
  const normalized = String(status || "DRAFT").toLowerCase();
  const key = `skills.status.${normalized}`;
  const translated = t(key);
  return translated === key ? String(status || "DRAFT") : translated;
};

const splitCsv = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const SEMVER_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:[-+][0-9A-Za-z.-]+)?$/;
const SLUG_RE = /^(?=.{3,64}$)[a-z0-9]+(?:-[a-z0-9]+)*$/;

const EMPTY_CREATOR_FORM = {
  name: "",
  slug: "",
  description: "",
  categories: "",
  tags: "",
  version: "",
};

const fmtDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return String(value).slice(0, 10);
  }
};

const ownerLabel = (t, owner) => {
  const screenName = owner?.twitterScreenName || "";
  if (screenName) return screenName.startsWith("@") ? screenName : `@${screenName}`;
  return owner?.twitterName || t("skills.creator.slugOwnerUnknown");
};

const slugAvailabilityText = (t, data) => {
  if (!data) return "";
  if (data.reserved) {
    return t("skills.creator.slugReserved", { slug: data.slug });
  }
  if (data.mine) {
    return t("skills.creator.slugOwnedByYou", {
      slug: data.slug,
      status: data.status || "DRAFT",
    });
  }
  if (data.available) {
    return t("skills.creator.slugAvailable", { slug: data.slug });
  }
  return t("skills.creator.slugTaken", {
    slug: data.slug,
    owner: ownerLabel(t, data.owner),
    status: data.status || "DRAFT",
  });
};

// 分类标签：预定义分类走 i18n，用户自建的任意分类（testing/marketplace/...）没有
// 对应 key，t() 会原样返回 key 字符串（如 "skills.cat.testing"）——那样很难看。
// 兜底：找不到译文就把原始分类名人性化显示（连字符转空格、词首大写）。
const catLabel = (t, category) => {
  const key = `skills.cat.${category}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return String(category)
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── 文件夹上传：skill 检测 + 客户端 store-zip 打包（无第三方依赖）──────────
// 「一个 skill = 一个直接含 SKILL.md 的目录」。检测所有这样的目录：
//   0 个 → 报错；1 个 → 自动选中；多个 → 用户选一个。
const dirOf = (p) => {
  const i = p.lastIndexOf("/");
  return i < 0 ? "" : p.slice(0, i);
};
const detectSkillRoots = (paths) => {
  const roots = new Set();
  for (const p of paths) {
    if (p.slice(p.lastIndexOf("/") + 1) === "SKILL.md") roots.add(dirOf(p));
  }
  return [...roots].sort();
};

// 选中某个 skill 根目录，抽出其子树并把该目录 rebase 成根（SKILL.md 到最外层），
// 丢弃根 README、其它 skill 等无关文件。
const filesUnderRoot = (files, root) => {
  const prefix = root ? `${root}/` : "";
  return files
    .filter((f) => f.path.startsWith(prefix) && f.path.length > prefix.length)
    .map((f) => ({ rel: f.path.slice(prefix.length), file: f.file }))
    .filter((f) => f.rel && !f.rel.includes("/..") && !f.rel.startsWith("."));
};

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
const crc32 = (bytes) => {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++)
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
};
const u16 = (n) => new Uint8Array([n & 255, (n >>> 8) & 255]);
const u32 = (n) =>
  new Uint8Array([
    n & 255,
    (n >>> 8) & 255,
    (n >>> 16) & 255,
    (n >>> 24) & 255,
  ]);

// entries: [{ path, bytes: Uint8Array }] → 不压缩(store, method 0) zip Blob。
// 后端校验器支持 method 0；文件都很小，无需 deflate。
const buildStoreZip = (entries) => {
  const enc = new TextEncoder();
  const parts = [];
  const central = [];
  let offset = 0;
  for (const e of entries) {
    const name = enc.encode(e.path);
    const crc = crc32(e.bytes);
    const size = e.bytes.length;
    parts.push(
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(size),
      u32(size),
      u16(name.length),
      u16(0),
      name,
      e.bytes,
    );
    central.push({ name, crc, size, offset });
    offset += 30 + name.length + size;
  }
  const cdStart = offset;
  let cdSize = 0;
  for (const c of central) {
    parts.push(
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(c.crc),
      u32(c.size),
      u32(c.size),
      u16(c.name.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(c.offset),
      c.name,
    );
    cdSize += 46 + c.name.length;
  }
  parts.push(
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(central.length),
    u16(central.length),
    u32(cdSize),
    u32(cdStart),
    u16(0),
  );
  return new Blob(parts, { type: "application/zip" });
};

// 收集可展示的 md 文件：始终含 SKILL.md；若后端未来在 latestVersion.files 里返回
// 其它 .md（guides/references 等），自动追加成多标签页。
const collectMdFiles = (latest) => {
  const files = [{ path: "SKILL.md", content: latest?.readmeMarkdown || "" }];
  for (const f of latest?.files || []) {
    if (
      f &&
      typeof f.content === "string" &&
      /\.md$/i.test(f.path || "") &&
      f.path !== "SKILL.md"
    ) {
      files.push({ path: f.path, content: f.content });
    }
  }
  return files;
};

const EmptyState = ({ title, body, action }) => (
  <div className="card skills-empty">
    <div className="skills-icon-box">
      <XIcons.IconBlocks size={18} />
    </div>
    <div>
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  </div>
);

const LoadingRows = () => (
  <div className="skills-grid">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="card skills-card" key={i}>
        <div className="skeleton" style={{ width: 92, height: 22 }} />
        <div
          className="skeleton"
          style={{ width: "70%", height: 20, marginTop: 18 }}
        />
        <div
          className="skeleton"
          style={{ width: "100%", height: 12, marginTop: 16 }}
        />
        <div
          className="skeleton"
          style={{ width: "82%", height: 12, marginTop: 8 }}
        />
      </div>
    ))}
  </div>
);

const SkillCard = ({ skill, onOpen }) => {
  const { t } = useT();
  const latest = skill.latestVersion;
  const deps = latest?.manifest?.xapiDependencies || [];
  return (
    <button className="card card-h skills-card" onClick={onOpen}>
      <div className="skills-card-head">
        <div className="skills-icon-box">
          <XIcons.IconBlocks size={16} />
        </div>
        <h3 className="skills-card-name">{skill.name || skill.slug}</h3>
        <span className="skills-card-ver">{latest?.version || "—"}</span>
      </div>
      <div className="skills-card-tags">
        {(skill.categories || []).slice(0, 3).map((category) => (
          <span className="tag" key={category}>
            {catLabel(t, category)}
          </span>
        ))}
      </div>
      <p className="skills-card-desc">
        {skill.description || t("skills.card.noDesc")}
      </p>
      <div className="skills-card-meta">
        <span>
          <XIcons.IconLayers size={12} />{" "}
          {t("skills.card.deps", { n: deps.length })}
        </span>
        <span>
          <XIcons.IconDownload size={12} /> {skill.installCount || 0}
        </span>
        <span className="skills-card-date">{fmtDate(skill.updatedAt)}</span>
      </div>
    </button>
  );
};

export const SkillsMarketPage = () => {
  const { navigate } = XShell.useRoute();
  const { t } = useT();
  const [q, setQ] = React.useState("");
  const [category, setCategory] = React.useState("all");
  const [state, setState] = React.useState({
    loading: true,
    error: "",
    data: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, loading: true, error: "" }));
      XApi.skillService
        .list({ q: q.trim(), category, limit: 30 })
        .then((data) => {
          if (!cancelled) setState({ loading: false, error: "", data });
        })
        .catch((err) => {
          if (!cancelled)
            setState({
              loading: false,
              error: err.message || t("skills.loadError"),
              data: null,
            });
        });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, category, t]);

  const items = state.data?.items || [];

  return (
    <main className="skills-shell">
      <section className="skills-page-head">
        <div>
          <div className="micro-mono">{t("skills.eyebrow")}</div>
          <h1>{t("skills.title")}</h1>
          <p>{t("skills.subtitle")}</p>
        </div>
        <div className="row gap-2 skills-head-actions">
          <button className="btn" onClick={() => navigate("skill")}>
            <XIcons.IconBook size={13} /> {t("skills.baseSkill")}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate("skills-console")}
          >
            <XIcons.IconPlus size={13} /> {t("skills.creatorConsole")}
          </button>
        </div>
      </section>

      <section className="card skills-toolbar">
        <label className="skills-search">
          <XIcons.IconSearch size={14} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("skills.search")}
          />
        </label>
        <div className="skills-segments">
          {CATEGORIES.map((item) => (
            <button
              key={item}
              className={category === item ? "active" : ""}
              onClick={() => setCategory(item)}
            >
              {t(`skills.cat.${item}`)}
            </button>
          ))}
        </div>
      </section>

      {state.error && (
        <div className="card skills-alert">
          <XIcons.IconInfo size={14} /> {state.error}
        </div>
      )}
      {state.loading ? (
        <LoadingRows />
      ) : items.length ? (
        <div className="skills-grid">
          {items.map((skill) => (
            <SkillCard
              key={skill.id || skill.slug}
              skill={skill}
              onOpen={() => navigate("skill-detail", { slug: skill.slug })}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title={t("skills.empty.title")}
          body={t("skills.empty.body")}
          action={
            <button
              className="btn btn-primary"
              onClick={() => navigate("skills-console")}
            >
              <XIcons.IconPlus size={13} /> {t("skills.empty.submit")}
            </button>
          }
        />
      )}
    </main>
  );
};

export const SkillDetailPage = () => {
  const { route, navigate } = XShell.useRoute();
  const { t } = useT();
  const slug = route.params.slug;
  const [state, setState] = React.useState({
    loading: true,
    error: "",
    skill: null,
  });
  const [activeFile, setActiveFile] = React.useState(0);
  const [copied, copy] = XPrim.useCopy();

  React.useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", skill: null });
    setActiveFile(0);
    XApi.skillService
      .detail(slug)
      .then((skill) => {
        if (!cancelled) setState({ loading: false, error: "", skill });
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            loading: false,
            error: err.message || t("skills.detail.notFound"),
            skill: null,
          });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, t]);

  const skill = state.skill;
  const latest = skill?.latestVersion;
  const manifest = latest?.manifest || {};
  const deps = manifest.xapiDependencies || [];
  const mdFiles = collectMdFiles(latest);
  const activeIdx = Math.min(activeFile, Math.max(mdFiles.length - 1, 0));
  const activeMd = mdFiles[activeIdx];
  const installCmd = `npx skills add https://github.com/xapi-labs/skill-market/tree/main/skills/${slug}`;
  const repositoryUrl = skill?.repo
    ? `https://github.com/${skill.repo}/tree/main/${skill.repoPath || `skills/${slug}`}`
    : "";
  const permissionEntries = [
    ["writesExternalState", t("skills.detail.permissionWrites")],
    ["mayChargeUserBalance", t("skills.detail.permissionCredits")],
    ["handlesPersonalData", t("skills.detail.permissionPersonal")],
  ].filter(([key]) => Boolean(manifest.permissions?.[key]));
  const examples = Array.isArray(manifest.metadata?.xapi?.examples)
    ? manifest.metadata.xapi.examples
    : Array.isArray(manifest.examples)
      ? manifest.examples
      : [];
  const handleCopyInstall = React.useCallback(() => {
    copy(installCmd);
    XApi.skillService
      .recordInstall(slug)
      .then((result) => {
        if (!result || typeof result.installCount !== "number") return;
        setState((prev) =>
          prev.skill
            ? {
                ...prev,
                skill: { ...prev.skill, installCount: result.installCount },
              }
            : prev,
        );
      })
      .catch(() => {});
  }, [copy, installCmd, slug]);

  return (
    <main className="skills-shell">
      <button
        className="btn btn-sm btn-ghost"
        onClick={() => navigate("skills")}
        style={{ marginBottom: 18 }}
      >
        <XIcons.IconArrowLeft size={13} /> {t("skills.detail.back")}
      </button>

      {state.loading && <LoadingRows />}
      {state.error && (
        <EmptyState
          title={t("skills.detail.unavailable")}
          body={state.error}
          action={
            <button className="btn" onClick={() => navigate("skills")}>
              {t("skills.detail.openMarket")}
            </button>
          }
        />
      )}
      {skill && (
        <>
          <section className="skills-detail-head">
            <div>
              <div
                className="row gap-2"
                style={{ flexWrap: "wrap", marginBottom: 14 }}
              >
                <StatusPill status="PUBLISHED" />
                {(skill.categories || []).map((category) => (
                  <span className="tag" key={category}>
                    {catLabel(t, category)}
                  </span>
                ))}
              </div>
              <h1>{skill.name || skill.slug}</h1>
              <p>{skill.description || t("skills.card.noDesc")}</p>
            </div>
            <div className="card skills-install">
              <div className="micro-mono">{t("skills.detail.install")}</div>
              <div className="skills-command">
                <code>{installCmd}</code>
                <button
                  className="btn btn-sm btn-ghost btn-icon"
                  onClick={handleCopyInstall}
                  title={t("skills.detail.copyInstall")}
                  aria-label={t("skills.detail.copyInstall")}
                >
                  {copied ? (
                    <XIcons.IconCheck size={12} />
                  ) : (
                    <XIcons.IconCopy size={12} />
                  )}
                </button>
              </div>
              <div className="skills-card-meta">
                <span>v{latest?.version || "—"}</span>
                <span>
                  <XIcons.IconDownload size={12} /> {skill.installCount || 0}
                </span>
                <span>{fmtDate(latest?.publishedAt)}</span>
              </div>
            </div>
          </section>

          <section className="skills-detail-grid">
            <div className="card skills-readme">
              <div className="skills-readme-head">
                <div className="skills-file-tabs">
                  {mdFiles.map((f, idx) => (
                    <button
                      key={f.path}
                      type="button"
                      className={`skills-file-tab ${idx === activeIdx ? "active" : ""}`}
                      onClick={() => setActiveFile(idx)}
                    >
                      {f.path}
                    </button>
                  ))}
                </div>
                <span className="micro-mono">{skill.slug}</span>
              </div>
              {activeMd?.content?.trim() ? (
                <div
                  className="skills-md"
                  dangerouslySetInnerHTML={{
                    __html: renderSkillMarkdown(activeMd.content),
                  }}
                />
              ) : (
                <p className="skills-muted">{t("skills.detail.noReadme")}</p>
              )}
            </div>
            <aside className="skills-detail-side">
              <div className="card skills-side-card">
                <div className="micro-mono">{t("skills.detail.deps")}</div>
                {deps.length ? (
                  <div className="skills-deps">
                    {deps.map((dep, idx) => (
                      <div
                        key={`${dep.type}-${dep.id || dep.serviceSlug || idx}`}
                      >
                        <span className="tag">{dep.type}</span>
                        <strong>
                          {dep.serviceSlug ||
                            dep.id ||
                            dep.endpointKey ||
                            "dependency"}
                        </strong>
                        {dep.purpose && <p>{dep.purpose}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="skills-muted">{t("skills.detail.noDeps")}</p>
                )}
              </div>
              <div className="card skills-side-card">
                <div className="micro-mono">{t("skills.detail.release")}</div>
                <dl className="skills-release-meta">
                  <div>
                    <dt>{t("skills.detail.owner")}</dt>
                    <dd>{ownerLabel(t, skill.owner)}</dd>
                  </div>
                  <div>
                    <dt>{t("skills.detail.installMetric")}</dt>
                    <dd>{skill.installCount || 0}</dd>
                  </div>
                  {repositoryUrl && (
                    <div>
                      <dt>{t("skills.detail.repository")}</dt>
                      <dd>
                        <a href={repositoryUrl} target="_blank" rel="noreferrer">
                          {skill.repoPath || `skills/${slug}`}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
                {(skill.versions || []).length > 0 && (
                  <div className="skills-version-history">
                    {(skill.versions || []).map((version) => (
                      <div key={version.id || version.version}>
                        <code>v{version.version}</code>
                        <span>{fmtDate(version.publishedAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {permissionEntries.length > 0 && (
                <div className="card skills-side-card">
                  <div className="micro-mono">
                    {t("skills.detail.permissions")}
                  </div>
                  <div className="skills-permissions">
                    {permissionEntries.map(([key, label]) => (
                      <span className="tag" key={key}>
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {examples.length > 0 && (
                <div className="card skills-side-card">
                  <div className="micro-mono">
                    {t("skills.detail.examples")}
                  </div>
                  <div className="skills-examples">
                    {examples.map((example, index) => (
                      <div key={`${example.title || "example"}-${index}`}>
                        <strong>{example.title || `Example ${index + 1}`}</strong>
                        <p>{example.prompt}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </section>
        </>
      )}
    </main>
  );
};

export const SkillCreatorPage = () => {
  const { navigate } = XShell.useRoute();
  const { t } = useT();
  const { authed, isApiKeyLogin, isEntity } = XShell.useAuth();
  const [skills, setSkills] = React.useState([]);
  const [selectedSlug, setSelectedSlug] = React.useState("");
  const [form, setForm] = React.useState(EMPTY_CREATOR_FORM);
  const [file, setFile] = React.useState(null);
  const [fileInputKey, setFileInputKey] = React.useState(0);
  const [folderInputKey, setFolderInputKey] = React.useState(0);
  const [folderFiles, setFolderFiles] = React.useState([]);
  const [skillRoots, setSkillRoots] = React.useState([]);
  const [githubUrl, setGithubUrl] = React.useState("");
  const [sourceTab, setSourceTab] = React.useState("github");
  const [packagePreview, setPackagePreview] = React.useState(null);
  const [pendingSource, setPendingSource] = React.useState(null);
  const [slugStatus, setSlugStatus] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [ok, setOk] = React.useState("");
  const metadataNameRef = React.useRef(null);
  const canUseCreator = (isEntity && !isApiKeyLogin) || isApiKeyLogin;
  const normalizedSlug = form.slug.trim().toLowerCase();
  const activeSlugStatus =
    slugStatus?.slug === normalizedSlug ? slugStatus : null;
  const slugBlocked = Boolean(
    activeSlugStatus?.data && !activeSlugStatus.data.available,
  );
  const selectedSkill = React.useMemo(
    () => skills.find((skill) => skill.slug === selectedSlug) || null,
    [selectedSlug, skills],
  );
  const selectedSubmission = selectedSkill?.submissions?.[0] || null;
  const selectedAutoReview = latestReview(selectedSubmission, "agent") ||
    latestReview(selectedSubmission, "static");
  const selectedHumanReview = latestReview(selectedSubmission, "human");
  const selectedPublish = latestPublish(selectedSubmission);

  const clearPreview = () => {
    setPackagePreview(null);
    setPendingSource(null);
    setForm(EMPTY_CREATOR_FORM);
    setSlugStatus(null);
    setError("");
    setOk("");
  };

  const resetCreatorState = () => {
    setPackagePreview(null);
    setPendingSource(null);
    setForm(EMPTY_CREATOR_FORM);
    setSlugStatus(null);
    setFile(null);
    setFolderFiles([]);
    setSkillRoots([]);
    setGithubUrl("");
    setNote("");
    setFileInputKey((key) => key + 1);
    setFolderInputKey((key) => key + 1);
  };

  const selectSourceTab = (tab) => {
    setSourceTab(tab);
    clearPreview();
  };

  const loadMine = React.useCallback(() => {
    if (!authed) return;
    XApi.skillService
      .mine()
      .then((rows) => {
        setSkills(rows);
        setSelectedSlug((current) =>
          current && rows.some((skill) => skill.slug === current)
            ? current
            : rows[0]?.slug || "",
        );
      })
      .catch((err) => setError(err.message || t("skills.loadError")));
  }, [authed, t]);

  React.useEffect(() => {
    loadMine();
  }, [loadMine]);

  React.useEffect(() => {
    const active = skills.some((skill) => {
      const submission = skill.submissions?.[0];
      const publish = latestPublish(submission);
      return (
        ACTIVE_CREATOR_STATUSES.has(submission?.status) ||
        ACTIVE_CREATOR_STATUSES.has(publish?.status)
      );
    });
    if (!active) return undefined;
    const timer = window.setInterval(loadMine, 3000);
    return () => window.clearInterval(timer);
  }, [loadMine, skills]);

  React.useEffect(() => {
    if (!authed || !canUseCreator || !packagePreview || !normalizedSlug) {
      setSlugStatus(null);
      return;
    }
    if (!SLUG_RE.test(normalizedSlug)) {
      setSlugStatus({ slug: normalizedSlug, state: "invalid" });
      return;
    }

    let cancelled = false;
    setSlugStatus({ slug: normalizedSlug, state: "checking" });
    const timer = window.setTimeout(() => {
      XApi.skillService
        .slugAvailability(normalizedSlug)
        .then((data) => {
          if (!cancelled) {
            setSlugStatus({ slug: normalizedSlug, state: "ready", data });
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setSlugStatus({
              slug: normalizedSlug,
              state: "error",
              error: err.message || "Slug check failed",
            });
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [authed, canUseCreator, packagePreview, normalizedSlug]);

  const applyPreview = (preview, source) => {
    setPackagePreview(preview);
    setPendingSource(source);
    setSelectedSlug(preview.slug || "");
    setSlugStatus(null);
    setForm({
      name: preview.name || preview.manifest?.name || "",
      slug: preview.slug || preview.manifest?.slug || "",
      description: preview.description || preview.manifest?.description || "",
      categories: (
        preview.categories ||
        preview.manifest?.categories ||
        []
      ).join(","),
      tags: (preview.tags || preview.manifest?.tags || []).join(","),
      version: preview.version || preview.manifest?.version || "",
    });
    setOk(
      t("skills.creator.parsedPackageStatus", {
        version: preview.version || preview.manifest?.version || "",
      }),
    );
    window.requestAnimationFrame(() => metadataNameRef.current?.focus());
  };

  // 选中文件夹：检测其中的 skill（含 SKILL.md 的目录）。只允许**恰好一个** skill：
  // 0 个 → 提示未找到；多个 → 报错拒绝（一次只能传一组 skill）。
  const onPickFolder = async (e) => {
    const list = Array.from(e.target.files || []).map((f) => ({
      path: f.webkitRelativePath || f.name,
      file: f,
    }));
    const roots = detectSkillRoots(list.map((f) => f.path));
    setFolderFiles(list);
    setSkillRoots(roots);
    clearPreview();
    if (!canUseCreator) return;
    if (roots.length !== 1) return;
    const root = roots[0];
    const selected = filesUnderRoot(list, root);
    if (!selected.some((f) => f.rel === "SKILL.md")) return;
    const zipFile = await buildFolderZipFile(selected, root);
    await previewZip(zipFile);
  };

  const singleRoot = skillRoots.length === 1 ? skillRoots[0] : null;
  const selectedSkillFiles = React.useMemo(
    () => (singleRoot !== null ? filesUnderRoot(folderFiles, singleRoot) : []),
    [folderFiles, singleRoot],
  );
  const folderReady =
    singleRoot !== null && selectedSkillFiles.some((f) => f.rel === "SKILL.md");

  async function buildFolderZipFile(
    files = selectedSkillFiles,
    root = singleRoot,
  ) {
    if (root === null || !files.some((f) => f.rel === "SKILL.md")) return null;
    const entries = [];
    for (const f of files) {
      entries.push({
        path: f.rel,
        bytes: new Uint8Array(await f.file.arrayBuffer()),
      });
    }
    const zipBlob = buildStoreZip(entries);
    const zipName = `${root.split("/").pop() || "skill"}.zip`;
    return new File([zipBlob], zipName, { type: "application/zip" });
  }

  const previewGitHub = async () => {
    if (!githubUrl.trim()) return;
    setBusy("github");
    setError("");
    setOk("");
    try {
      const preview = await XApi.skillService.previewGitHub(githubUrl.trim());
      applyPreview(preview, { type: "github", url: githubUrl.trim() });
    } catch (err) {
      setError(err.message || "GitHub preview failed");
    } finally {
      setBusy("");
    }
  };

  const previewZip = async (zipFile) => {
    if (!zipFile) return;
    setBusy("upload");
    setError("");
    setOk("");
    try {
      const preview = await XApi.skillService.previewZip(zipFile);
      applyPreview(preview, { type: "zip", file: zipFile });
    } catch (err) {
      setError(err.message || "Package preview failed");
    } finally {
      setBusy("");
    }
  };

  const previewFolder = async () => {
    const zipFile = await buildFolderZipFile();
    await previewZip(zipFile);
  };

  const submit = async () => {
    if (!packagePreview || !pendingSource) return;
    const slug = form.slug.trim().toLowerCase();
    if (!slug || !form.name.trim()) {
      setError("Name and slug are required");
      return;
    }
    if (!SLUG_RE.test(slug)) {
      setError(t("skills.creator.slugInvalid"));
      return;
    }
    const versionNumber = form.version.trim();
    if (!SEMVER_RE.test(versionNumber)) {
      setError("Version must be a semantic version, e.g. 0.1.0");
      return;
    }
    setBusy("submit");
    setError("");
    setOk("");
    try {
      const availability =
        activeSlugStatus?.data && activeSlugStatus.slug === slug
          ? activeSlugStatus.data
          : await XApi.skillService.slugAvailability(slug);
      setSlugStatus({ slug, state: "ready", data: availability });
      if (!availability.available) {
        setError(slugAvailabilityText(t, availability));
        return;
      }

      let skill = skills.find((item) => item.slug === slug);
      if (!skill) {
        skill = await XApi.skillService.create({
          slug,
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          categories: splitCsv(form.categories),
          tags: splitCsv(form.tags),
        });
      }

      let version;
      const metadata = {
        version: versionNumber,
        name: form.name.trim(),
        description: form.description.trim(),
        categories: splitCsv(form.categories),
        tags: splitCsv(form.tags),
      };
      if (pendingSource.type === "github") {
        version = await XApi.skillService.importGitHub(
          slug,
          pendingSource.url,
          metadata,
        );
      } else {
        version = await XApi.skillService.uploadZip(
          slug,
          pendingSource.file,
          metadata,
        );
      }

      const result = await XApi.skillService.submit(
        slug,
        version.id,
        note.trim() || undefined,
        version.sourceType,
      );
      setOk(`Submitted for review: ${result.submission?.id || "queued"}`);
      resetCreatorState();
      setSelectedSlug(slug);
      await XApi.skillService.mine().then(setSkills);
    } catch (err) {
      const slugConflict =
        err.data?.code && String(err.data.code).startsWith("SKILL_SLUG")
          ? err.data
          : null;
      if (slugConflict) {
        setSlugStatus({ slug: slugConflict.slug || slug, state: "ready", data: slugConflict });
      }
      setError(
        slugConflict
          ? slugAvailabilityText(t, slugConflict)
          : err.message || "Submit failed",
      );
    } finally {
      setBusy("");
    }
  };

  if (!authed) {
    return (
      <main className="skills-shell">
        <EmptyState
          title={t("skills.creator.signInTitle")}
          body={t("skills.creator.signInBody")}
          action={
            <button
              className="btn btn-primary"
              onClick={() => navigate("login")}
            >
              {t("skills.creator.signIn")}
            </button>
          }
        />
      </main>
    );
  }

  return (
    <main className="skills-shell">
      <section className="skills-page-head">
        <div>
          <div className="micro-mono">{t("skills.creator.eyebrow")}</div>
          <h1>{t("skills.creator.title")}</h1>
          <p>{t("skills.creator.subtitle")}</p>
        </div>
        <button className="btn" onClick={() => navigate("skills")}>
          <XIcons.IconGrid size={13} /> {t("skills.creator.marketplace")}
        </button>
      </section>

      {(!canUseCreator || isApiKeyLogin) && (
        <div className="card skills-alert">
          <XIcons.IconShield size={14} />
          {isApiKeyLogin
            ? t("skills.creator.apiKeyNote")
            : t("skills.creator.entityNote")}
        </div>
      )}
      {error && (
        <div className="card skills-alert err">
          <XIcons.IconInfo size={14} /> {error}
        </div>
      )}
      {ok && (
        <div className="card skills-alert ok">
          <XIcons.IconCheck size={14} /> {ok}
        </div>
      )}

      <section className="skills-console-grid">
        <div className="card skills-form">
          <div className="micro-mono">{t("skills.creator.step1")}</div>
          <p className="skills-source-hint">{t("skills.creator.sourceHint")}</p>
          <div className="skills-source-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={sourceTab === "github"}
              className={`skills-source-tab ${sourceTab === "github" ? "active" : ""}`}
              onClick={() => selectSourceTab("github")}
            >
              <XIcons.IconGithub size={12} /> {t("skills.creator.githubUrl")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sourceTab === "folder"}
              className={`skills-source-tab ${sourceTab === "folder" ? "active" : ""}`}
              onClick={() => selectSourceTab("folder")}
            >
              <XIcons.IconGrid size={12} /> {t("skills.creator.folderTab")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sourceTab === "zip"}
              className={`skills-source-tab ${sourceTab === "zip" ? "active" : ""}`}
              onClick={() => selectSourceTab("zip")}
            >
              <XIcons.IconArrowUp size={12} /> {t("skills.creator.packageZip")}
            </button>
          </div>

          {sourceTab === "github" && (
            <div className="skills-source-panel">
              <label>
                <span className="field-label">
                  {t("skills.creator.githubUrl")}
                </span>
                <input
                  className="input mono"
                  value={githubUrl}
                  onChange={(e) => {
                    setGithubUrl(e.target.value);
                    clearPreview();
                  }}
                  placeholder="https://github.com/xapi-labs/skill-market/tree/main/skills/twitter-research"
                />
              </label>
              <button
                className="btn skills-source-btn"
                disabled={
                  !githubUrl.trim() || busy === "github" || !canUseCreator
                }
                onClick={previewGitHub}
              >
                {busy === "github" ? (
                  <span className="spinner" />
                ) : (
                  <XIcons.IconGithub size={13} />
                )}{" "}
                {t("skills.creator.parseGitHub")}
              </button>
            </div>
          )}

          {sourceTab === "folder" && (
            <div className="skills-source-panel">
              <label className="skills-folder-pick">
                <input
                  key={folderInputKey}
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  aria-label={t("skills.creator.chooseFolder")}
                  onChange={onPickFolder}
                />
                <XIcons.IconArrowUp size={13} />{" "}
                {t("skills.creator.chooseFolder")}
              </label>

              {folderFiles.length > 0 && skillRoots.length === 0 && (
                <p className="skills-source-warn">
                  {t("skills.creator.noSkillFound")}
                </p>
              )}

              {skillRoots.length > 1 && (
                <div className="skills-source-warn skills-multi-warn">
                  <XIcons.IconInfo size={13} />
                  <div>
                    {t("skills.creator.multiSkill")}
                    <div className="skills-multi-list">
                      {skillRoots.map((root) => (
                        <span key={root} className="mono">
                          {root || "(root)"}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {singleRoot !== null && selectedSkillFiles.length > 0 && (
                <div className="skills-filelist">
                  <div className="skills-filelist-head">
                    <span className="field-label">
                      {t("skills.creator.filesInSkill")}
                    </span>
                    <span
                      className={`skills-filelist-badge ${folderReady ? "ok" : "err"}`}
                    >
                      {folderReady ? (
                        <>
                          <XIcons.IconCheck size={11} />{" "}
                          {t("skills.creator.skillMdRoot")}
                        </>
                      ) : (
                        <>
                          <XIcons.IconInfo size={11} /> SKILL.md{" "}
                          {t("skills.creator.missing")}
                        </>
                      )}
                    </span>
                  </div>
                  {selectedSkillFiles.map((f) => (
                    <div
                      key={f.rel}
                      className={`skills-file-row ${f.rel === "SKILL.md" ? "primary" : ""}`}
                    >
                      <span className="mono">{f.rel}</span>
                    </div>
                  ))}
                </div>
              )}

              <button
                className="btn skills-source-btn"
                disabled={!folderReady || busy === "upload" || !canUseCreator}
                onClick={previewFolder}
              >
                {busy === "upload" ? (
                  <span className="spinner" />
                ) : (
                  <XIcons.IconArrowUp size={13} />
                )}{" "}
                {t("skills.creator.parseFolder")}
              </button>
            </div>
          )}

          {sourceTab === "zip" && (
            <div className="skills-source-panel">
              <label>
                <span className="field-label">
                  {t("skills.creator.packageZip")}
                </span>
                <input
                  key={fileInputKey}
                  className="input"
                  type="file"
                  accept=".zip,application/zip"
                  aria-label={t("skills.creator.packageZip")}
                  onChange={(e) => {
                    const next = e.target.files?.[0] || null;
                    setFile(next);
                    clearPreview();
                    if (next && canUseCreator) previewZip(next);
                  }}
                />
              </label>
              <button
                className="btn skills-source-btn"
                disabled={!file || busy === "upload" || !canUseCreator}
                onClick={() => previewZip(file)}
              >
                {busy === "upload" ? (
                  <span className="spinner" />
                ) : (
                  <XIcons.IconArrowUp size={13} />
                )}{" "}
                {t("skills.creator.parseZip")}
              </button>
            </div>
          )}

          <div className="skills-version-box">
            <div className="micro-mono">
              {t("skills.creator.packageParsed")}
            </div>
              <strong>
                {packagePreview
                ? `${form.slug || packagePreview.slug || "skill"} · v${form.version || packagePreview.version || "0.1.0"} · ${packagePreview.packageSha256?.slice(0, 10) || ""}`
                : t("skills.creator.noValidated")}
              </strong>
          </div>
        </div>

        <form
          className="card skills-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="micro-mono">{t("skills.creator.step2")}</div>
          {!packagePreview && (
            <div className="skills-metadata-state" role="status">
              {busy === "upload" || busy === "github" ? (
                <span className="spinner" />
              ) : (
                <XIcons.IconInfo size={14} />
              )}
              <div>
                <strong>
                  {busy === "upload" || busy === "github"
                    ? t("skills.creator.metadataParsing")
                    : t("skills.creator.metadataWaiting")}
                </strong>
                <span>{t("skills.creator.metadataLocked")}</span>
              </div>
            </div>
          )}
          <label>
            <span className="field-label">{t("skills.creator.name")}</span>
            <input
              ref={metadataNameRef}
              className="input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Twitter Research"
              required
              disabled={!packagePreview}
            />
          </label>
          <label>
            <span className="field-label">{t("skills.creator.slug")}</span>
            <input
              className="input mono"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="twitter-research"
              required
              disabled={!packagePreview}
            />
            {packagePreview && activeSlugStatus && (
              <p
                className={`skills-slug-status ${
                  activeSlugStatus.state === "checking"
                    ? "checking"
                    : activeSlugStatus.state === "invalid" ||
                        activeSlugStatus.state === "error" ||
                        slugBlocked
                      ? "err"
                      : activeSlugStatus.data?.mine
                        ? "warn"
                        : "ok"
                }`}
              >
                {activeSlugStatus.state === "checking"
                  ? t("skills.creator.slugChecking")
                  : activeSlugStatus.state === "invalid"
                    ? t("skills.creator.slugInvalid")
                    : activeSlugStatus.state === "error"
                      ? activeSlugStatus.error
                      : slugAvailabilityText(t, activeSlugStatus.data)}
              </p>
            )}
          </label>
          <label>
            <span className="field-label">{t("skills.creator.version")}</span>
            <input
              className="input mono"
              value={form.version}
              onChange={(e) => setForm({ ...form, version: e.target.value })}
              placeholder="0.1.0"
              required
              disabled={!packagePreview}
            />
          </label>
          <label>
            <span className="field-label">
              {t("skills.creator.description")}
            </span>
            <textarea
              className="input skills-textarea"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Research public Twitter/X activity through xapi."
              disabled={!packagePreview}
            />
          </label>
          <label>
            <span className="field-label">
              {t("skills.creator.categories")}
            </span>
            <input
              className="input"
              value={form.categories}
              onChange={(e) => setForm({ ...form, categories: e.target.value })}
              placeholder="social,research"
              disabled={!packagePreview}
            />
          </label>
          <label>
            <span className="field-label">{t("skills.creator.tags")}</span>
            <input
              className="input"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="twitter,x"
              disabled={!packagePreview}
            />
          </label>

          <label>
            <span className="field-label">
              {t("skills.creator.reviewerNote")}
            </span>
            <textarea
              className="input skills-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("skills.creator.reviewerNotePh")}
              disabled={!packagePreview}
            />
          </label>
          <button
            className="btn btn-primary"
            disabled={
              !packagePreview || busy === "submit" || !canUseCreator || slugBlocked
            }
          >
            {busy === "submit" ? (
              <span className="spinner" />
            ) : (
              <XIcons.IconCheck size={13} />
            )}{" "}
            {t("skills.creator.submitPackage")}
          </button>
        </form>
      </section>

      <section className="card skills-mine">
        <div
          className="row between"
          style={{ padding: 16, borderBottom: "1px solid var(--line)" }}
        >
          <div>
            <div className="micro-mono">{t("skills.creator.mySkills")}</div>
            <p className="skills-muted" style={{ margin: "4px 0 0" }}>
              {t("skills.creator.mySkillsSub")}
            </p>
          </div>
          <button className="btn btn-sm" onClick={loadMine}>
            <XIcons.IconRefresh size={12} /> {t("skills.creator.refresh")}
          </button>
        </div>
        {skills.length ? (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("skills.creator.colSkill")}</th>
                  <th>{t("skills.creator.colStatus")}</th>
                  <th>{t("skills.creator.colVersions")}</th>
                  <th>{t("skills.creator.colSubmission")}</th>
                  <th>{t("skills.creator.colUpdated")}</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((skill) => {
                  const latestSubmission = skill.submissions?.[0];
                  return (
                    <tr key={skill.id}>
                      <td>
                        <button
                          className="skills-link"
                          onClick={() => setSelectedSlug(skill.slug)}
                        >
                          {skill.slug}
                        </button>
                        <div className="skills-muted">{skill.name}</div>
                      </td>
                      <td>
                        <StatusPill status={skill.status} />
                      </td>
                      <td>
                        {(skill.versions || [])
                          .map((v) => v.version)
                          .join(", ") || "—"}
                      </td>
                      <td>
                        {latestSubmission ? (
                          <StatusPill status={latestSubmission.status} />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{fmtDate(skill.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 18 }} className="skills-muted">
            {t("skills.creator.noDrafts")}
          </div>
        )}
        {selectedSkill && (
          <div className="skills-submission-detail">
            <div className="row between gap-2 skills-submission-head">
              <div>
                <div className="micro-mono">
                  {t("skills.creator.submissionProgress")}
                </div>
                <strong>
                  {selectedSkill.slug}
                  {selectedSkill.versions?.[0]?.version
                    ? ` · v${selectedSkill.versions[0].version}`
                    : ""}
                </strong>
              </div>
              {selectedSubmission && (
                <StatusPill
                  status={selectedSubmission.status}
                  label={localizedStatus(t, selectedSubmission.status)}
                />
              )}
            </div>
            <div className="skills-submission-flow">
              <div>
                <span>{t("skills.creator.stageSubmitted")}</span>
                <strong>
                  {selectedSubmission
                    ? localizedStatus(t, selectedSubmission.status)
                    : t("skills.creator.notSubmitted")}
                </strong>
                <small>{fmtDate(selectedSubmission?.createdAt)}</small>
              </div>
              <div>
                <span>{t("skills.creator.stageAutoReview")}</span>
                <strong>
                  {selectedAutoReview
                    ? localizedStatus(t, selectedAutoReview.status)
                    : t("skills.creator.waiting")}
                </strong>
                <small>
                  {selectedAutoReview?.reviewNote ||
                    t("skills.creator.noReviewResult")}
                </small>
              </div>
              <div>
                <span>{t("skills.creator.stageHumanReview")}</span>
                <strong>
                  {selectedHumanReview
                    ? localizedStatus(t, selectedHumanReview.status)
                    : t("skills.creator.waiting")}
                </strong>
                <small>
                  {selectedHumanReview?.reviewNote ||
                    t("skills.creator.noReviewResult")}
                </small>
              </div>
              <div>
                <span>{t("skills.creator.stagePublish")}</span>
                <strong>
                  {selectedPublish
                    ? localizedStatus(t, selectedPublish.status)
                    : t("skills.creator.notStarted")}
                </strong>
                <small>
                  {selectedPublish?.ciStatus
                    ? `CI ${selectedPublish.ciStatus}${
                        selectedPublish.ciConclusion
                          ? ` · ${selectedPublish.ciConclusion}`
                          : ""
                      }`
                    : selectedPublish?.error || t("skills.creator.noPublishJob")}
                </small>
              </div>
            </div>
            {(selectedPublish?.ciUrl || selectedPublish?.commitSha) && (
              <div className="skills-submission-links">
                {selectedPublish.ciUrl && (
                  <a href={selectedPublish.ciUrl} target="_blank" rel="noreferrer">
                    {t("skills.creator.openCi")}
                  </a>
                )}
                {selectedPublish.commitSha && selectedPublish.repo && (
                  <a
                    href={`https://github.com/${selectedPublish.repo}/commit/${selectedPublish.commitSha}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("skills.creator.openCommit")}
                  </a>
                )}
              </div>
            )}
            {selectedPublish?.error && (
              <div className="skills-submission-error">
                {selectedPublish.error}
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default SkillsMarketPage;
